/**
 * Upload files into the corpus, optionally pinned to one collection.
 *
 * Extracted from the Library page so a collection can hold its own copy. Filing used to
 * be possible ONLY here, on Pustaka, through a dropdown that appeared after files were
 * chosen and defaulted to "no collection" — so the path from an empty collection was:
 * leave the collection, land on Pustaka, pick files, notice a new dropdown, remember
 * which collection you came from. Missing that one step used to be permanent.
 *
 * `fixedCollection` is what closes that. When set, the panel uploads into that collection
 * and does not offer the choice at all: there is no way to be on a collection's own page,
 * press upload, and have the file land somewhere else.
 */
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Panel } from "@/components/primitives.tsx";
import { ApiError, api } from "@/lib/api.ts";
import type { IngestionJob } from "@/lib/api-types.ts";

const SOURCE_TYPES = ["upload", "JDIH", "OpenData", "PPID", "SatuData", "BPS"] as const;

/** From `UploadStatus` in `services/upload.py`. Guessing at "completed"/"partial" —
 *  names the API does not use — left the poller running forever against a job that
 *  had finished, hammering an endpoint every 3s for the life of the tab. `skipped` is
 *  terminal AND benign: a structured open-data source is tool-retrieved at query time
 *  (ADR-0035), or the bytes were a duplicate that was not re-embedded. */
const TERMINAL_JOB_STATUSES = new Set([
  "done",
  "skipped",
  "failed",
  "permanently_failed",
  "needs_review",
]);

/**
 * Which collection an upload lands in.
 *
 * The pinned collection WINS over whatever the local picker holds. Reading the picker
 * first would let a selection made on Pustaka outlive a navigation into a collection —
 * pressing upload on one collection's page and having the file land in another is the
 * exact failure this panel exists to remove.
 */
export function uploadTarget(
  fixedCollection: { id: string } | undefined,
  chosenCollectionId: string,
): string {
  return fixedCollection ? fixedCollection.id : chosenCollectionId;
}

export function UploadPanel({
  collections,
  fixedCollection,
  onUploaded,
}: {
  collections: Array<{ id: string; name: string }>;
  /** Pin every upload to this collection and hide the picker. */
  fixedCollection?: { id: string; name: string };
  onUploaded: () => void;
}) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [collectionId, setCollectionId] = useState("");
  const [sourceType, setSourceType] = useState<string>("upload");
  const [job, setJob] = useState<IngestionJob | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const targetCollectionId = uploadTarget(fixedCollection, collectionId);

  const upload = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      for (const file of files) form.append("files", file);
      form.append("source_type", sourceType);
      // Absent, not blank: an empty collection_id would be a 422 against a UUID field.
      if (targetCollectionId) form.append("collection_id", targetCollectionId);
      return api.upload(form);
    },
    onSuccess: (result) => {
      setJob(result);
      setFiles([]);
      if (input.current) input.current.value = "";
      onUploaded();
    },
  });

  // Ingestion is async and the job store is in-memory and single-replica, so polling
  // may 404 after a restart even though the document rows survive. Stop on either.
  const [polling, setPolling] = useState(false);
  useEffect(() => {
    if (!job?.job_id) return;
    setPolling(true);
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const next = await api.job(job.job_id);
        if (cancelled) return;
        setJob(next);
        if (TERMINAL_JOB_STATUSES.has(next.status)) {
          setPolling(false);
          clearInterval(timer);
          onUploaded();
        }
      } catch {
        setPolling(false);
        clearInterval(timer);
      }
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [job?.job_id, onUploaded]);

  return (
    <section aria-label={t("library.uploadTitle")}>
      <div className="eyebrow">
        {fixedCollection
          ? t("collection.uploadHere", { name: fixedCollection.name })
          : t("library.uploadTitle")}
      </div>

      {/* WHERE IT WILL LAND, BEFORE ANY FILE IS CHOSEN. The old panel revealed the
          destination only after files were picked, so the commonest outcome was an
          upload filed nowhere by a reader who never saw the choice. */}
      {!fixedCollection ? (
        <label className="row upload-to">
          {/* `whiteSpace: nowrap` — the eyebrow is uppercase and letter-spaced, so
              "Masukkan ke koleksi" wrapped to two lines beside a full-width select and
              the pair read as two unrelated controls. */}
          <span className="eyebrow" style={{ whiteSpace: "nowrap" }}>
            {t("library.uploadTo")}
          </span>
          <select
            className="input"
            value={collectionId}
            onChange={(event) => setCollectionId(event.target.value)}
            aria-label={t("library.uploadTo")}
          >
            <option value="">{t("library.uploadNone")}</option>
            {collections.map((collection) => (
              <option key={collection.id} value={collection.id}>
                {collection.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <div
        className="drop"
        role="button"
        tabIndex={0}
        onClick={() => input.current?.click()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") input.current?.click();
        }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          setFiles(Array.from(event.dataTransfer.files));
        }}
      >
        <p style={{ margin: 0 }}>{t("library.uploadDrop")}</p>
        <p className="hint">{t("library.uploadFormats")}</p>
        {/* An explicit button, because "or click to choose" in grey text is not an
            affordance — the box read as drag-and-drop only. `tabIndex={-1}`: the box
            itself is already the keyboard target, and two stops on one control is worse
            than one. The click bubbles to the box's own handler, so there is still a
            single code path opening the picker. */}
        <span className="btn btn-sm drop-browse" tabIndex={-1} aria-hidden="true">
          {t("library.uploadBrowse")}
        </span>
        <input
          ref={input}
          type="file"
          multiple
          className="sr-only"
          aria-label={t("library.uploadTitle")}
          accept=".pdf,.docx,.xlsx,.xls,.csv,.html,.htm,.txt,.png,.jpg,.jpeg,.tiff"
          onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
        />
      </div>

      {files.length > 0 ? (
        <div className="stack" style={{ marginTop: "var(--s3)" }}>
          <ul className="stack" style={{ margin: 0, paddingLeft: "1.2em" }}>
            {files.map((file) => (
              <li key={file.name} style={{ fontSize: 13.5 }}>
                {file.name} <span className="faint mono">{Math.round(file.size / 1024)} KB</span>
              </li>
            ))}
          </ul>

          <div className="row" style={{ flexWrap: "wrap" }}>
            <label className="row">
              <span className="eyebrow">{t("library.uploadSourceType")}</span>
              <select
                className="input"
                value={sourceType}
                onChange={(event) => setSourceType(event.target.value)}
                aria-label={t("library.uploadSourceType")}
              >
                {SOURCE_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <span className="spacer" />
            <Button variant="primary" onClick={() => upload.mutate()} disabled={upload.isPending}>
              {upload.isPending ? t("library.uploadSubmitting") : t("library.uploadSubmit")}
            </Button>
          </div>
        </div>
      ) : null}

      {upload.error ? (
        <Panel tone="red" title={t("errors.generic")}>
          {upload.error instanceof ApiError ? upload.error.message : null}
        </Panel>
      ) : null}

      {job ? (
        <Panel tone={job.status === "failed" ? "red" : "accent"} title={t("library.uploadStatus")}>
          <div className="row">
            <span className="mono">{job.status}</span>
            {polling ? <span className="pulse" aria-hidden="true" /> : null}
          </div>
          <ul style={{ margin: "var(--s2) 0 0", paddingLeft: "1.2em" }}>
            {job.documents?.map((document) => (
              <li key={document.document_id}>
                {document.filename} — {document.status}
                {document.detail ? <span className="faint"> · {document.detail}</span> : null}
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </section>
  );
}
